import { lazy, type ComponentType } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppShell } from './AppShell';
import { RequireAuth, RequireRole } from '@/features/auth/guards';

// Lazy-load each route's page by its NAMED export, so every route is its own
// chunk (the catalog JSON and Three.js stack never load until needed).
function page<M extends Record<string, ComponentType<unknown>>>(
  loader: () => Promise<M>,
  name: keyof M,
) {
  return lazy(async () => ({ default: (await loader())[name] }));
}

const HomePage = page(() => import('@/pages/HomePage'), 'HomePage');
const AllServicesPage = page(() => import('@/pages/AllServicesPage'), 'AllServicesPage');
const GoldPage = page(() => import('@/pages/calculators/GoldPage'), 'GoldPage');
const SkillingPage = page(() => import('@/pages/calculators/SkillingPage'), 'SkillingPage');
const PvmPage = page(() => import('@/pages/calculators/PvmPage'), 'PvmPage');
const QuestsPage = page(() => import('@/pages/calculators/QuestsPage'), 'QuestsPage');
const MinigamesPage = page(() => import('@/pages/calculators/MinigamesPage'), 'MinigamesPage');
const DiariesPage = page(() => import('@/pages/calculators/DiariesPage'), 'DiariesPage');
const CombatAchievementsPage = page(
  () => import('@/pages/calculators/CombatAchievementsPage'),
  'CombatAchievementsPage',
);
const IronmanGatheringPage = page(
  () => import('@/pages/calculators/IronmanGatheringPage'),
  'IronmanGatheringPage',
);
const AccountsPage = page(() => import('@/pages/calculators/AccountsPage'), 'AccountsPage');
const ItemsPage = page(() => import('@/pages/calculators/ItemsPage'), 'ItemsPage');
const StorePage = page(() => import('@/pages/StorePage'), 'StorePage');
const OffersCategoryPage = page(() => import('@/pages/OffersCategoryPage'), 'OffersCategoryPage');
const ProductDetailPage = page(() => import('@/pages/ProductDetailPage'), 'ProductDetailPage');
const SearchPage = page(() => import('@/pages/SearchPage'), 'SearchPage');
const FaqPage = page(() => import('@/pages/FaqPage'), 'FaqPage');
const ContactPage = page(() => import('@/pages/ContactPage'), 'ContactPage');
const LegalPage = page(() => import('@/pages/LegalPage'), 'LegalPage');
const LoginPage = page(() => import('@/pages/LoginPage'), 'LoginPage');
const RegisterPage = page(() => import('@/pages/RegisterPage'), 'RegisterPage');
const ForgotPasswordPage = page(() => import('@/pages/ForgotPasswordPage'), 'ForgotPasswordPage');
const ResetPasswordPage = page(() => import('@/pages/ResetPasswordPage'), 'ResetPasswordPage');
const SuccessPage = page(() => import('@/pages/SuccessPage'), 'SuccessPage');
const CartPage = page(() => import('@/pages/CartPage'), 'CartPage');
const AccountPage = page(() => import('@/pages/AccountPage'), 'AccountPage');
const OrdersPage = page(() => import('@/pages/OrdersPage'), 'OrdersPage');
const WishlistPage = page(() => import('@/pages/WishlistPage'), 'WishlistPage');
const MyChatPage = page(() => import('@/pages/MyChatPage'), 'MyChatPage');
const ReviewsPage = page(() => import('@/pages/ReviewsPage'), 'ReviewsPage');
const NotFoundPage = page(() => import('@/pages/NotFoundPage'), 'NotFoundPage');

// SaleTool admin dashboard — replaces the old /admin, /support, /chat-directory pages
// with one unified, full-screen sidebar dashboard (its own chrome, outside AppShell).
const DashboardLayout = page(() => import('@/features/dashboard/DashboardLayout'), 'DashboardLayout');
const HomeView = page(() => import('@/features/dashboard/views/HomeView'), 'HomeView');
const OrdersView = page(() => import('@/features/dashboard/views/OrdersView'), 'OrdersView');
const ProductsView = page(() => import('@/features/dashboard/views/ProductsView'), 'ProductsView');
const CustomersView = page(() => import('@/features/dashboard/views/CustomersView'), 'CustomersView');
const VisitorDataView = page(() => import('@/features/dashboard/views/VisitorDataView'), 'VisitorDataView');
const UserDataView = page(() => import('@/features/dashboard/views/UserDataView'), 'UserDataView');
const DiscountsView = page(() => import('@/features/dashboard/views/DiscountsView'), 'DiscountsView');
const ReviewsView = page(() => import('@/features/dashboard/views/ReviewsView'), 'ReviewsView');
const AnalyticsView = page(() => import('@/features/dashboard/views/AnalyticsView'), 'AnalyticsView');
const LiveChatView = page(() => import('@/features/dashboard/views/LiveChatView'), 'LiveChatView');
const SettingsView = page(() => import('@/features/dashboard/views/SettingsView'), 'SettingsView');

export const router = createBrowserRouter([
  {
    path: '/',
    element: <AppShell />,
    children: [
      { index: true, element: <HomePage /> },
      { path: 'services', element: <AllServicesPage /> },
      { path: 'services/gold', element: <GoldPage /> },
      { path: 'services/skilling', element: <SkillingPage /> },
      { path: 'services/pvm', element: <PvmPage /> },
      { path: 'services/quests', element: <QuestsPage /> },
      { path: 'services/minigames', element: <MinigamesPage /> },
      { path: 'services/diaries', element: <DiariesPage /> },
      { path: 'services/combat-achievements', element: <CombatAchievementsPage /> },
      { path: 'services/ironman-gathering', element: <IronmanGatheringPage /> },
      // Legacy alias preserved from the prototype.
      { path: 'services/im-gathering-misc', element: <IronmanGatheringPage /> },
      { path: 'services/accounts', element: <AccountsPage /> },
      { path: 'services/items', element: <ItemsPage /> },
      { path: 'store', element: <StorePage /> },
      { path: 'products', element: <OffersCategoryPage /> },
      { path: 'products/:category', element: <OffersCategoryPage /> },
      { path: 'product/:id', element: <ProductDetailPage /> },
      { path: 'search', element: <SearchPage /> },
      { path: 'faq', element: <FaqPage /> },
      { path: 'reviews', element: <ReviewsPage /> },
      { path: 'contact', element: <ContactPage /> },
      { path: 'legal/:doc', element: <LegalPage /> },
      { path: 'login', element: <LoginPage /> },
      { path: 'register', element: <RegisterPage /> },
      { path: 'forgot-password', element: <ForgotPasswordPage /> },
      { path: 'reset-password', element: <ResetPasswordPage /> },
      { path: 'success', element: <SuccessPage /> },
      { path: 'cart', element: <CartPage /> },
      { path: 'account', element: <RequireAuth><AccountPage /></RequireAuth> },
      { path: 'orders', element: <RequireAuth><OrdersPage /></RequireAuth> },
      { path: 'wishlist', element: <RequireAuth><WishlistPage /></RequireAuth> },
      { path: 'my-chat', element: <MyChatPage /> },
      // Legacy staff routes now redirect into the unified dashboard.
      { path: 'support', element: <Navigate to="/admin/chat" replace /> },
      { path: 'chat-directory', element: <Navigate to="/admin/chat" replace /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  {
    // Full-screen SaleTool dashboard — its own chrome, outside the storefront shell.
    path: '/admin',
    element: (
      <RequireRole staff>
        <DashboardLayout />
      </RequireRole>
    ),
    children: [
      { index: true, element: <HomeView /> },
      { path: 'orders', element: <OrdersView /> },
      { path: 'products', element: <ProductsView /> },
      { path: 'customers', element: <CustomersView /> },
      { path: 'visitors', element: <VisitorDataView /> },
      { path: 'users', element: <UserDataView /> },
      { path: 'discounts', element: <DiscountsView /> },
      { path: 'reviews', element: <ReviewsView /> },
      { path: 'catalog', element: <Navigate to="/admin/products" replace /> },
      { path: 'analytics', element: <AnalyticsView /> },
      { path: 'chat', element: <LiveChatView /> },
      { path: 'settings', element: <SettingsView /> },
    ],
  },
]);
